/**
 * Soft-delete mutation for the agent detail page.
 *
 * Mirrors useAgentMarkSpam's shape so the page wires it the same way.
 * On success: invalidate the detail, the inbox list, and the counts
 * so the deleted ticket disappears from the user's surface. The page
 * also navigates away after the mutation resolves - without that,
 * the agent saw "nothing happened" because the cached detail kept
 * rendering even after deleted_at was set in the DB.
 */
import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";

import api from "@/lib/api";

import { agentTicketCountsQueryKey } from "./useAgentTicketCounts";
import { agentTicketDetailQueryPrefix } from "./useAgentTicketDetail";

export function useSoftDeleteTicket(
  ticketNumber: number,
): UseMutationResult<void, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.delete(`/agent/tickets/${ticketNumber}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: agentTicketDetailQueryPrefix,
      });
      void queryClient.invalidateQueries({
        queryKey: ["agent", "tickets", "list"],
      });
      void queryClient.invalidateQueries({
        queryKey: agentTicketCountsQueryKey,
      });
    },
  });
}
